import * as React from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslation } from 'next-i18next';
import tw from 'twin.macro';
import { UseFormReturn } from 'react-hook-form';

import { Button } from '@restworld/ui-ds-base';
import { CreateEmployerRestaurantPayload } from '@restworld/data-services';
import { useAnalytics } from '@restworld/utils-common';

import { activeAddressIdAtom, googleAddresInputValueAtom } from './google-address-input-result';
import { RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import useRestaurantOnboardingMutationQuery from './restaurant-onboarding-mutation-query';
import { activeRestaurantOnboardingStateAtom, isManualEntryAtom } from '.';
import {
  useAccountAlreadyExistOrNotMutation,
  useCheckRestaurantDuplicate,
  useFormatRestaurantOnboardingData,
  useGettingStartedPage,
} from './restaurant-onboarding-modal.hooks';

export const useRestaurantCreateSubmit = ({
  methods,
}: {
  methods: UseFormReturn<RestaurantOnboardingPayload, any>;
}) => {
  const formatFormData = useFormatRestaurantOnboardingData();

  const { mutate: onOnboardingRestaurant, isPending: isRestaurantOnboarding } = useRestaurantOnboardingMutationQuery();

  const onRestaurantOnboardingFormSubmit = methods.handleSubmit(
    React.useCallback(
      (data) => {
        const formattedData = formatFormData(data);
        // onboarding new restaurant
        onOnboardingRestaurant(formattedData as CreateEmployerRestaurantPayload);
      },
      [formatFormData, onOnboardingRestaurant]
    )
  );
  return {
    onRestaurantOnboardingFormSubmit,
    isRestaurantOnboarding,
  };
};
export const RestaurantOnboardingFooter: React.FC<{
  methods: UseFormReturn<RestaurantOnboardingPayload, any>;
}> = ({ methods }) => {
  const { t: tC } = useTranslation('common');
  const googleInputAddressValue = useAtomValue(googleAddresInputValueAtom);
  const activeItem = useAtomValue(activeAddressIdAtom);
  const [activeUI, setActiveUI] = useAtom(activeRestaurantOnboardingStateAtom);
  const isManualEntry = useAtomValue(isManualEntryAtom);
  const isInputFilled = googleInputAddressValue.length > 0;
  const { onRestaurantOnboardingFormSubmit, isRestaurantOnboarding } = useRestaurantCreateSubmit({ methods });
  const isGettingStartedPage = useGettingStartedPage();

  const { mutate: checkUserExistence, isPending } = useAccountAlreadyExistOrNotMutation();
  const { mutateAsync: checkDuplicate, isPending: isCheckingDuplicate } = useCheckRestaurantDuplicate();
  const { trackPHEvent } = useAnalytics();

  React.useEffect(() => {
    Object.keys(methods.formState.errors).forEach((key) => {
      const element = document.getElementById(key);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }, [methods.formState]);

  const runDuplicateCheckThen = React.useCallback(
    async (onPass: () => void) => {
      if (!isManualEntry) {
        onPass();
        return;
      }
      try {
        await checkDuplicate({ restaurant_name: methods.getValues('restaurant_name') });
        onPass();
      } catch {
        // Error handled by mutation onError (shows restaurant_already_exists)
      }
    },
    [checkDuplicate, isManualEntry, methods]
  );

  const handleContinue = React.useCallback(() => {
    if (activeUI === 'select_restaurant_from_google') {
      setActiveUI('magic_ai_loading');
      return;
    }
    if (isGettingStartedPage) {
      if (activeUI === 'restaurant_onboarding_form') {
        methods.trigger().then((v) => {
          if (v) {
            runDuplicateCheckThen(() => {
              setActiveUI('restaurant_owner_onboarding_form');
              trackPHEvent('freemium_employer_confirms_restaurant_data', {});
            });
          }
        });
      } else if (activeUI === 'restaurant_owner_onboarding_form') {
        methods.trigger().then((v) => {
          if (v)
            checkUserExistence({
              email: methods.getValues('email'),
              phone_number: methods.getValues('phone_number'),
            });
        });
      }
      return;
    }
    if (activeUI === 'restaurant_onboarding_form') {
      methods.trigger().then((v) => {
        if (v) {
          runDuplicateCheckThen(() => onRestaurantOnboardingFormSubmit());
        }
      });
      return;
    }
  }, [
    activeUI,
    checkUserExistence,
    isGettingStartedPage,
    methods,
    onRestaurantOnboardingFormSubmit,
    runDuplicateCheckThen,
    setActiveUI,
    trackPHEvent,
  ]);

  const shouldHideButton = React.useMemo(
    () =>
      activeUI === 'restaurant_onboarding_successfull' ||
      (activeUI === 'select_restaurant_from_google' && !isInputFilled) ||
      activeUI === 'magic_ai_loading',
    [activeUI, isInputFilled]
  );

  if (shouldHideButton) return null;

  return (
    <div tw="flex justify-center items-center w-full md:(max-w-[38rem] min-w-[38rem] mx-auto relative -left-2)">
      <Button
        loading={isRestaurantOnboarding || isPending || isCheckingDuplicate}
        onClick={handleContinue}
        disabled={activeUI === 'select_restaurant_from_google' && !activeItem && !isManualEntry}
        size="md"
        twStyle={tw`w-full`}
        variant="primary"
      >
        {tC('continue')}
      </Button>
    </div>
  );
};
